import { t } from '@bia-auction/shared-i18n';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ApiError } from '../lib/api';

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

/**
 * Last-resort render error boundary (M9, ADR 0023). Expected API errors are handled per-call
 * (TanStack Query `onError` toasts); this catches an *unexpected* render crash so the whole SPA
 * doesn't blank out. When the thrown error is an {@link ApiError} carrying the server correlation
 * id, it's shown so a user can quote it to support (it ties to the server log line).
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Client-side breadcrumb; server-side failures are already logged with the same requestId.
    console.error('[error-boundary]', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    const requestId = error instanceof ApiError ? error.requestId : undefined;
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="w-full max-w-md rounded-lg border p-6 text-center">
          <h1 className="text-lg font-semibold">{t('common.errorBoundary.title')}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{t('common.errorBoundary.message')}</p>
          {requestId && (
            <p className="mt-3 text-xs text-muted-foreground">
              {t('common.errorBoundary.requestId')}: <code>{requestId}</code>
            </p>
          )}
          <button
            type="button"
            className="mt-4 rounded-md border px-4 py-2 text-sm"
            onClick={() => window.location.reload()}
          >
            {t('common.errorBoundary.reload')}
          </button>
        </div>
      </div>
    );
  }
}
